import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Scheme, Theme } from '@/styles/tokens';
import { ThemeContext, type ThemeContextValue } from './theme-context';
import { readThemePreference, writeThemePreference, type ThemePreference } from './storage';

interface ThemeProviderProps {
  children: ReactNode;
  /** Overrides the stored preference. Intended for tests and Storybook-style previews. */
  initial?: Partial<ThemePreference>;
}

export function ThemeProvider({ children, initial }: ThemeProviderProps) {
  const [preference, setPreference] = useState<ThemePreference>(() => ({
    ...readThemePreference(),
    ...initial,
  }));

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.scheme = preference.scheme;
    root.dataset.theme = preference.theme;
    // Keeps native controls and scrollbars aligned with the active theme.
    root.style.colorScheme = preference.theme;
    writeThemePreference(preference);
  }, [preference]);

  const setScheme = useCallback((scheme: Scheme) => {
    setPreference((current) => (current.scheme === scheme ? current : { ...current, scheme }));
  }, []);

  const setTheme = useCallback((theme: Theme) => {
    setPreference((current) => (current.theme === theme ? current : { ...current, theme }));
  }, []);

  const toggleTheme = useCallback(() => {
    setPreference((current) => ({
      ...current,
      theme: current.theme === 'dark' ? 'light' : 'dark',
    }));
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ ...preference, setScheme, setTheme, toggleTheme }),
    [preference, setScheme, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
